import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward, Wand2, Volume2, VolumeX, Share2 } from "lucide-react";
import { Slider } from "../components/ui/slider";
import { api, resolveAudio } from "../lib/api";
import { stopAllMediaExcept, onExclusivePlay } from "../lib/mediaBus";
import { toast } from "sonner";

export const FX_ORDER = ["Clean", "Comms", "Canyon", "Ghost", "Demon Boss", "Tiny", "Underwater", "Robotic"];

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
};

export const VoiceFXLab = ({ kicker, title, sub } = {}) => {
  const [samples, setSamples] = useState([]);
  const [idx, setIdx] = useState(0);
  const [fx, setFx] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [everPlayed, setEverPlayed] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [vol, setVol] = useState(1);

  const audioRef = useRef(null);
  const pendingSeek = useRef(null);
  const pendingPlay = useRef(false);
  const prevVol = useRef(1);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const srcRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => { api.get("/voice-samples").then((r) => setSamples(r.data)).catch(() => {}); }, []);

  const sample = samples[idx];
  const fxList = sample ? sample.variants.map((v) => v.fx) : [];
  const variant = sample ? (sample.variants.find((v) => v.fx === fx) || sample.variants[0]) : null;
  const src = variant ? resolveAudio(variant.audio_path) : null;

  useEffect(() => {
    if (sample && !sample.variants.some((v) => v.fx === fx)) setFx(sample.variants[0]?.fx || null);
    // eslint-disable-next-line
  }, [idx, samples]);

  const ensureGraph = () => {
    if (ctxRef.current || !audioRef.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      const source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ctx.destination);
      ctxRef.current = ctx; analyserRef.current = analyser; srcRef.current = source;
    } catch (e) { console.debug("FX visualizer graph unavailable:", e); }
  };

  const draw = () => {
    const canvas = canvasRef.current, analyser = analyserRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (canvas.width !== w * dpr) { canvas.width = w * dpr; canvas.height = h * dpr; }
    const g = canvas.getContext("2d");
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    let data = null;
    if (analyser) { data = new Uint8Array(analyser.frequencyBinCount); analyser.getByteFrequencyData(data); }
    const grad = g.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, "#F4E4C8"); grad.addColorStop(0.5, "#C8A97E"); grad.addColorStop(1, "#F4E4C8");
    g.fillStyle = grad;
    const half = 30;                                  // bars per side
    const usable = data ? Math.floor(data.length * 0.55) : 0; // drop dead high bands
    const midY = h / 2;
    const bw = w / (half * 2);
    for (let k = 0; k < half; k++) {
      const di = data ? Math.floor((k / half) * usable) : 0;
      const v = data ? data[di] / 255 : 0;
      const idle = playing ? 0.03 : 0.05 + 0.03 * Math.sin(Date.now() / 380 + k * 0.5);
      const bh = Math.max(h * idle, v * (h - 6));
      const y = midY - bh / 2;
      g.fillRect(w / 2 + k * bw + 1, y, bw - 2, bh);       // right of center
      g.fillRect(w / 2 - (k + 1) * bw + 1, y, bw - 2, bh); // mirrored left
    }
    rafRef.current = requestAnimationFrame(draw);
  };

  useEffect(() => {
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line
  }, [playing]);

  const applyPending = () => {
    const a = audioRef.current;
    a.volume = vol;
    if (pendingSeek.current != null) {
      try { a.currentTime = a.duration ? Math.min(pendingSeek.current, a.duration) : pendingSeek.current; } catch (e) { console.debug("seek deferred:", e); }
      setCur(a.currentTime);
      pendingSeek.current = null;
    }
    setDur(a.duration || 0);
    if (pendingPlay.current) {
      stopAllMediaExcept(a);
      a.play().then(() => setPlaying(true)).catch(() => {});
      pendingPlay.current = false;
    }
  };

  const togglePlay = () => {
    const a = audioRef.current;
    ensureGraph();
    if (ctxRef.current && ctxRef.current.state === "suspended") ctxRef.current.resume();
    if (playing) { a.pause(); setPlaying(false); }
    else {
      stopAllMediaExcept(a);
      a.volume = vol;
      setEverPlayed(true);
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  };
  useEffect(() => onExclusivePlay((el) => { if (el !== audioRef.current) setPlaying(false); }), []);

  const changeVol = (v) => {
    const nv = Math.max(0, Math.min(1, v[0] / 100));
    setVol(nv);
    if (audioRef.current) audioRef.current.volume = nv;
  };
  const toggleMute = () => {
    if (vol > 0) { prevVol.current = vol; changeVol([0]); }
    else { changeVol([(prevVol.current || 1) * 100]); }
  };

  // Select a voice + a specific variant (Clean or its FX), retaining playback position/state.
  const selectVoiceFx = (i, nf) => {
    const a = audioRef.current;
    const sameVoice = i === idx;
    if (sameVoice && nf === fx) return;
    ensureGraph();
    if (ctxRef.current && ctxRef.current.state === "suspended") ctxRef.current.resume();
    pendingSeek.current = sameVoice ? (a ? a.currentTime : 0) : 0;
    pendingPlay.current = true;
    setEverPlayed(true);
    if (!sameVoice) { setIdx(i); setCur(0); }
    setFx(nf);
  };

  const copyLink = (i) => {
    const s = samples[i];
    const chosenFx = i === idx ? fx : "Clean";
    const url = `${window.location.origin}/#fxlab?v=${slugify(s.title)}&fx=${slugify(chosenFx)}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Share link copied", { description: `${s.title} · ${chosenFx}` }))
      .catch(() => toast.error("Couldn't copy link"));
  };

  // Deep-link: #fxlab?v=<voice-slug>&fx=<fx-slug> -> select, scroll & try to play.
  useEffect(() => {
    if (!samples.length) return;
    const hash = window.location.hash || "";
    if (!hash.startsWith("#fxlab") || hash.indexOf("?") < 0) return;
    const params = new URLSearchParams(hash.slice(hash.indexOf("?") + 1));
    const v = params.get("v"), f = params.get("fx");
    if (!v) return;
    const i = samples.findIndex((s) => slugify(s.title) === v);
    if (i < 0) return;
    const target = samples[i];
    let chosenFx = "Clean";
    if (f) {
      const m = target.variants.find((x) => slugify(x.fx) === f);
      if (m) chosenFx = m.fx;
    }
    setIdx(i); setFx(chosenFx); setCur(0);
    pendingSeek.current = 0; pendingPlay.current = true;
    setTimeout(() => {
      document.getElementById("fxlab")?.scrollIntoView({ behavior: "smooth" });
      const a = audioRef.current;
      if (a) { stopAllMediaExcept(a); a.volume = vol; a.play().then(() => setPlaying(true)).catch(() => {}); }
    }, 500);
    // eslint-disable-next-line
  }, [samples]);

  const seek = (v) => { const a = audioRef.current; if (a && dur) { a.currentTime = (v[0] / 100) * dur; setCur(a.currentTime); } };

  if (!samples.length) return null;

  return (
    <section id="fxlab" className="border-b border-white/5 bg-[#0d0e13] relative overflow-hidden">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-16 md:py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="text-center mb-8">
            <span className="font-mono text-xs tracking-[0.3em] text-[#C8A97E] uppercase inline-flex items-center gap-2"><Wand2 size={14} /> {kicker || "Interactive Showcase · Character Demos"}</span>
            <h2 className="font-head font-bold tracking-tight mt-3 whitespace-nowrap" style={{ fontSize: "clamp(0.9rem, 3.8vw, 2.1rem)" }}>{title || "Hear the Performance. Toggle the FX."}</h2>
            <p className="text-[#A1A1AA] mt-3 text-sm md:text-base max-w-xl mx-auto leading-relaxed">{sub || "Select a voice, press play, then toggle to compare the clean, raw booth recording with its unique customized FX."}</p>
          </div>
        </motion.div>

        <audio ref={audioRef} src={src} preload="metadata"
          onLoadedMetadata={applyPending}
          onTimeUpdate={(e) => setCur(e.target.currentTime)}
          onDurationChange={(e) => setDur(e.target.duration || 0)}
          onEnded={() => setPlaying(false)} />

        <div className="rounded-2xl border border-white/10 bg-[#12141A] p-4 sm:p-6 shadow-2xl">
          {/* now playing */}
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#C8A97E]">Now Playing</div>
              <div className="font-head text-lg font-medium truncate">{sample?.title}</div>
            </div>
            <span className="shrink-0 text-[11px] font-mono px-2.5 py-1 rounded-sm bg-[#C8A97E]/10 border border-[#C8A97E]/30 text-[#C8A97E]">{fx}</span>
          </div>

          {/* visualizer */}
          <div className="relative rounded-xl overflow-hidden bg-[#0B0C10] border border-white/5">
            <canvas ref={canvasRef} data-testid="fx-visualizer" className="w-full h-24 sm:h-32 block" />
          </div>

          {/* transport */}
          <div className="flex items-center gap-2 sm:gap-3 mt-4">
            <button onClick={() => selectVoiceFx((idx - 1 + samples.length) % samples.length, "Clean")} data-testid="fx-prev" className="shrink-0 h-9 w-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#C8A97E]/50 transition-colors"><SkipBack size={15} /></button>
            <button onClick={togglePlay} data-testid="fx-play" className={`shrink-0 h-12 w-12 rounded-full bg-[#C8A97E] text-[#0B0C10] flex items-center justify-center hover:scale-105 transition-transform ${!everPlayed && !playing ? "pulse-ring" : ""}`}>
              {playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-0.5" />}
            </button>
            <button onClick={() => selectVoiceFx((idx + 1) % samples.length, "Clean")} data-testid="fx-next" className="shrink-0 h-9 w-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#C8A97E]/50 transition-colors"><SkipForward size={15} /></button>
            <Slider value={[dur ? (cur / dur) * 100 : 0]} onValueChange={seek} max={100} step={0.1} className="flex-1" />
            <span className="font-mono text-[11px] sm:text-xs text-[#A1A1AA] w-16 sm:w-20 text-right shrink-0">{fmt(cur)} / {fmt(dur)}</span>
          </div>

          {/* volume */}
          <div className="flex items-center gap-2 mt-3">
            <button onClick={toggleMute} data-testid="fx-mute" aria-label="Mute" className="shrink-0 text-[#A1A1AA] hover:text-[#C8A97E] transition-colors">
              {vol > 0 ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <Slider value={[vol * 100]} onValueChange={changeVol} max={100} step={1} data-testid="fx-volume" className="w-28 sm:w-40" />
            <span className="font-mono text-[11px] text-[#6b6b73] w-8 shrink-0">{Math.round(vol * 100)}</span>
          </div>

          {/* voice table */}
          <div className="mt-5 rounded-xl border border-white/10 overflow-hidden">
            <div className="flex items-center gap-1.5 sm:gap-3 px-2.5 sm:px-4 py-2 bg-[#0B0C10] text-[10px] font-mono uppercase tracking-[0.15em] text-[#6b6b73]">
              <span className="hidden sm:block w-6 text-center shrink-0">#</span>
              <span className="flex-1 min-w-0">Voice</span>
              <span className="hidden sm:block w-11 text-right shrink-0" />
              <span className="w-[132px] sm:w-[192px] text-center shrink-0">Clean · FX</span>
              <span className="w-7 sm:w-8 text-center shrink-0">Share</span>
            </div>
            <div className="divide-y divide-white/5">
              {samples.map((s, i) => {
                const fxName = (s.variants.find((v) => v.fx !== "Clean") || s.variants[s.variants.length - 1] || {}).fx;
                const activeRow = i === idx;
                return (
                  <div key={s.id} data-testid={`fx-row-${i}`}
                    className={`flex items-center gap-1.5 sm:gap-3 px-2.5 sm:px-4 py-2.5 transition-colors ${activeRow ? "bg-[#C8A97E]/[0.08]" : "hover:bg-white/[0.03]"}`}>
                    <span className={`hidden sm:block w-6 shrink-0 font-mono text-[11px] text-center ${activeRow ? "text-[#C8A97E]" : "text-[#6b6b73]"}`}>{(i + 1).toString().padStart(2, "0")}</span>
                    <button onClick={() => selectVoiceFx(i, activeRow ? fx : "Clean")} data-testid={`fx-sample-${i}`}
                      className={`flex-1 min-w-0 text-left text-sm truncate transition-colors ${activeRow ? "text-[#C8A97E] font-semibold" : "text-[#D4D4D8] hover:text-white"}`}>
                      {s.title}
                    </button>
                    <span className="hidden sm:block w-11 text-right shrink-0 font-mono text-[10px] sm:text-[11px] text-[#8b8b93]">{fmt(s.duration)}</span>
                    <div className="inline-flex p-0.5 rounded-md bg-[#0B0C10] border border-white/10 shrink-0">
                      <button onClick={() => selectVoiceFx(i, "Clean")} data-testid={`fx-clean-${i}`}
                        className={`w-[62px] sm:w-[90px] py-1.5 text-[11px] sm:text-xs font-semibold rounded transition-all whitespace-nowrap ${activeRow && fx === "Clean" ? "bg-[#272A35] text-white" : "text-[#8b8b93] hover:text-white"}`}>Clean</button>
                      <button onClick={() => selectVoiceFx(i, fxName)} data-testid={`fx-effect-${i}`}
                        className={`w-[62px] sm:w-[90px] px-0.5 py-1.5 text-[11px] sm:text-xs font-semibold rounded transition-all whitespace-nowrap ${activeRow && fx === fxName ? "bg-[#C8A97E] text-[#0B0C10]" : "text-[#8b8b93] hover:text-white"}`}>{fxName}</button>
                    </div>
                    <button onClick={() => copyLink(i)} data-testid={`fx-share-${i}`} aria-label={`Share ${s.title}`} title="Copy share link"
                      className="w-7 sm:w-8 h-8 shrink-0 flex items-center justify-center rounded-md text-[#8b8b93] hover:text-[#C8A97E] hover:bg-white/5 transition-colors">
                      <Share2 size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
